/**
 * Dinheiro mora aqui, e sempre em centavos inteiros.
 *
 * Nada de float: 0.1 + 0.2 !== 0.3 em ponto flutuante, e essa diferenca vira
 * divergencia de centavo no fechamento do rolê. Todo valor no sistema e um
 * `number` inteiro representando centavos (R$ 12,34 => 1234).
 */
export class MoneyError extends Error {
    constructor(message) {
        super(message);
        this.name = "MoneyError";
    }
}
/** Um valor so e valido em centavos se for inteiro e finito. */
export function isCents(value) {
    return typeof value === "number" && Number.isInteger(value) && Number.isFinite(value);
}
export function assertCents(value, field = "valor") {
    if (!isCents(value)) {
        throw new MoneyError(`O campo "${field}" precisa ser um valor inteiro em centavos.`);
    }
}
/**
 * Distribui `totalCents` entre os pesos informados usando o metodo do maior resto.
 *
 * Cada um leva o piso da sua fatia; os centavos que sobram sao entregues, um a um,
 * para quem ficou com a maior parte fracionaria (empate: maior peso, depois ordem
 * de entrada, para o resultado ser sempre deterministico).
 *
 * Garantia: `distribute(t, w).reduce(soma) === t`, sempre. E isso que faz
 * R$ 100,00 dividido por 3 virar 33,34 + 33,33 + 33,33 em vez de perder um centavo.
 */
export function distribute(totalCents, weights) {
    assertCents(totalCents, "total");
    if (weights.length === 0)
        return [];
    for (const weight of weights) {
        if (!Number.isFinite(weight) || weight < 0) {
            throw new MoneyError("Os pesos da divisao precisam ser numeros maiores ou iguais a zero.");
        }
    }
    const totalWeight = weights.reduce((acc, weight) => acc + weight, 0);
    if (totalWeight <= 0) {
        throw new MoneyError("A soma dos pesos da divisao precisa ser maior que zero.");
    }
    // O sinal e tratado a parte para que o arredondamento de valores negativos
    // (um estorno, por exemplo) seja o espelho exato do positivo.
    const sign = totalCents < 0 ? -1 : 1;
    const absolute = Math.abs(totalCents);
    const exact = weights.map((weight) => (absolute * weight) / totalWeight);
    const result = exact.map((value) => Math.floor(value));
    const distributed = result.reduce((acc, value) => acc + value, 0);
    const remainder = absolute - distributed;
    const order = exact
        .map((value, index) => ({
        index,
        fraction: value - Math.floor(value),
        weight: weights[index],
    }))
        .filter((entry) => entry.weight > 0)
        .sort((a, b) => b.fraction - a.fraction || b.weight - a.weight || a.index - b.index);
    // `remainder` e sempre menor que a quantidade de pesos positivos, entao uma
    // unica passada pela lista ordenada basta.
    for (let i = 0; i < remainder && i < order.length; i += 1) {
        const entry = order[i];
        result[entry.index] += 1;
    }
    return sign < 0 ? result.map((value) => -value) : result;
}
/** Divide igualmente entre `count` pessoas, distribuindo a sobra de centavos. */
export function splitEqually(totalCents, count) {
    if (!Number.isInteger(count) || count <= 0) {
        throw new MoneyError("A quantidade de pessoas na divisao precisa ser maior que zero.");
    }
    return distribute(totalCents, new Array(count).fill(1));
}
export function sumCents(values) {
    return values.reduce((acc, value) => acc + value, 0);
}
/** R$ 1.234,56 — formatacao pt-BR, a unica que aparece na interface. */
export function formatCents(cents, options = {}) {
    const { showSymbol = true } = options;
    const formatted = (cents / 100).toLocaleString("pt-BR", {
        style: showSymbol ? "currency" : "decimal",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return formatted;
}
/**
 * Le um texto digitado pelo usuario e devolve centavos.
 * Aceita "12,34", "12.34", "1.234,56" e "R$ 1.234,56". Devolve `null` se nao der.
 */
export function parseAmountToCents(input) {
    const cleaned = input.replace(/[^\d,.-]/g, "").trim();
    if (cleaned === "" || cleaned === "-")
        return null;
    const lastComma = cleaned.lastIndexOf(",");
    const lastDot = cleaned.lastIndexOf(".");
    let normalized;
    if (lastComma === -1 && lastDot === -1) {
        normalized = cleaned;
    }
    else if (lastComma > lastDot) {
        // Virgula e o separador decimal: "1.234,56"
        normalized = cleaned.replace(/\./g, "").replace(",", ".");
    }
    else {
        const decimals = cleaned.length - lastDot - 1;
        // Um ponto com ate 2 casas e decimal ("12.34"); qualquer outra coisa e milhar.
        normalized =
            decimals > 0 && decimals <= 2 && cleaned.indexOf(".") === lastDot
                ? cleaned.replace(/,/g, "")
                : cleaned.replace(/[.,]/g, "");
    }
    const value = Number(normalized);
    if (!Number.isFinite(value))
        return null;
    return Math.round(value * 100);
}
//# sourceMappingURL=money.js.map